import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Masthead,
  MastheadBrand,
  MastheadLogo,
  MastheadMain,
  MastheadContent,
  MenuToggle,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageContext,
  PageSidebar,
  PageSidebarBody,
  SkipToContent,
  Title,
} from '@patternfly/react-core';
import { IAppRoute, IAppRouteGroup, routes as buildRoutes } from '@app/routes';
import QuestionCircleIcon from '@patternfly/react-icons/dist/esm/icons/question-circle-icon';
import SignOutAltIcon from '@patternfly/react-icons/dist/esm/icons/sign-out-alt-icon';
import UserIcon from '@patternfly/react-icons/dist/esm/icons/user-icon';
import { buildTimeBranding, useSiteConfig } from '@app/config/SiteConfigContext';
import { getCurrentUserIdentity, isAuthenticated, signOutUser } from '@app/utils/portalAuth';

interface IAppLayout {
  children: React.ReactNode;
}

const mastheadMenuPopper = {
  position: 'right' as const,
  direction: 'down' as const,
  minWidth: '200px',
  maxWidth: '300px',
  enableFlip: false,
};

const HelpMenu: React.FunctionComponent<{
  docsHref: string;
  supportMailto?: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}> = ({ docsHref, supportMailto, isOpen, onOpenChange }) => {
  const navigate = useNavigate();

  return (
    <Dropdown
      isOpen={isOpen}
      onSelect={() => onOpenChange(false)}
      onOpenChange={onOpenChange}
      popperProps={mastheadMenuPopper}
      toggle={(toggleRef) => (
        <MenuToggle
          ref={toggleRef}
          id="help-btn"
          variant="plain"
          icon={<QuestionCircleIcon />}
          aria-label="Help"
          isExpanded={isOpen}
          onClick={() => onOpenChange(!isOpen)}
        />
      )}
    >
      <DropdownList>
        <DropdownItem
          key="docs"
          onClick={() => {
            navigate(docsHref);
          }}
        >
          Documentation
        </DropdownItem>
        {supportMailto && (
          <DropdownItem
            key="support"
            onClick={() => {
              window.location.href = supportMailto;
            }}
          >
            Support
          </DropdownItem>
        )}
      </DropdownList>
    </Dropdown>
  );
};

const UserAccountMenu: React.FunctionComponent<{
  email: string;
  onSignOut: () => void | Promise<void>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}> = ({ email, onSignOut, isOpen, onOpenChange }) => (
  <Dropdown
    isOpen={isOpen}
    onSelect={() => onOpenChange(false)}
    onOpenChange={onOpenChange}
    popperProps={{
      position: 'right',
      direction: 'down',
      width: 'trigger',
      enableFlip: false,
    }}
    toggle={(toggleRef) => (
      <MenuToggle
        ref={toggleRef}
        id="user-menu-toggle"
        onClick={() => onOpenChange(!isOpen)}
        isExpanded={isOpen}
        isFullHeight
        icon={<UserIcon />}
        aria-label="Account menu"
      >
        {email}
      </MenuToggle>
    )}
  >
    <DropdownList>
      <DropdownItem key="logout" icon={<SignOutAltIcon />} onClick={onSignOut}>
        Logout
      </DropdownItem>
    </DropdownList>
  </Dropdown>
);

/** Masthead logo that toggles the Page-managed sidebar (must render under Page). */
const MastheadNavLogo: React.FunctionComponent<{ src: string; productName: string }> = ({
  src,
  productName,
}) => {
  const { onSidebarToggle, isSidebarOpen } = React.useContext(PageContext);
  return (
    <MastheadLogo
      component="button"
      type="button"
      onClick={onSidebarToggle}
      aria-label={`${productName} navigation`}
      aria-expanded={isSidebarOpen}
    >
      <img src={src} alt="" width="38" height="38" />
    </MastheadLogo>
  );
};

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const site = useSiteConfig();
  const displayName = site?.displayName || buildTimeBranding.displayName || 'QuickCheck';
  const productName = site?.productName || buildTimeBranding.applicationName || '';
  const supportEmail = site?.supportEmail;
  const logoUrl = site?.branding?.logoUrl || `/images/${buildTimeBranding.logoFile || 'favicon.ico'}`;
  const [isUserAuthenticated, setIsUserAuthenticated] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState<string | null>(null);
  const [openMenu, setOpenMenu] = React.useState<'help' | 'user' | null>(null);

  React.useEffect(() => {
    const checkAuth = async () => {
      const auth = await isAuthenticated();
      setIsUserAuthenticated(auth);
      if (auth) {
        setCurrentUser(await getCurrentUserIdentity());
      }
    };
    checkAuth();
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    setIsUserAuthenticated(false);
    setCurrentUser(null);
    window.location.href = '/';
  };

  const accountEmail = isUserAuthenticated ? currentUser : null;

  const masthead = (
    <Masthead
      className={accountEmail ? 'app-masthead-with-account' : undefined}
      display={{ default: 'inline' }}
    >
      <MastheadMain>
        <MastheadBrand data-codemods>
          <MastheadNavLogo src={logoUrl} productName={productName || displayName} />
          <Title headingLevel="h1" size="2xl" style={{ margin: 0 }}>
            {displayName}
            {productName ? ` ${productName}` : ''}
          </Title>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>
        <div className="app-masthead-help">
          <HelpMenu
            docsHref="/docs"
            supportMailto={
              supportEmail
                ? `mailto:${supportEmail}?subject=I have a question about ${productName || displayName}`
                : undefined
            }
            isOpen={openMenu === 'help'}
            onOpenChange={(isOpen) => setOpenMenu(isOpen ? 'help' : null)}
          />
        </div>
        {accountEmail && (
          <div className="app-masthead-account">
            <UserAccountMenu
              email={accountEmail}
              onSignOut={handleSignOut}
              isOpen={openMenu === 'user'}
              onOpenChange={(isOpen) => setOpenMenu(isOpen ? 'user' : null)}
            />
          </div>
        )}
      </MastheadContent>
    </Masthead>
  );

  const location = useLocation();

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavItem key={`${route.label}-${index}`} id={`${route.label}-${index}`} isActive={route.path === location.pathname}>
      <NavLink to={route.path} end>
        {route.label}
      </NavLink>
    </NavItem>
  );

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => (
    <NavExpandable
      key={`${group.label}-${groupIndex}`}
      id={`${group.label}-${groupIndex}`}
      title={group.label}
      isActive={group.routes.some((route) => route.path === location.pathname)}
    >
      {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
    </NavExpandable>
  );

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {buildRoutes(
          site?.siteId || buildTimeBranding.siteId || 'QuickCheck',
          site?.productName || buildTimeBranding.applicationName || ''
        ).map(
          (route, idx) => route.label && (!route.routes ? renderNavItem(route, idx) : renderNavGroup(route, idx))
        )}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar>
      <PageSidebarBody>{Navigation}</PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer && primaryContentContainer.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );

  const footer = (
    <footer
      style={{
        position: 'fixed',
        bottom: 0,
        width: '100%',
        textAlign: 'center',
        padding: '1rem',
        backgroundColor: 'var(--pf-t--global--background--color--secondary--default)',
        borderTop:
          'var(--pf-t--global--border--width--divider--default) solid var(--pf-t--global--border--color--default)',
      }}
    >
      <NavLink
        to="/terms"
        style={{ color: 'var(--pf-t--global--text--color--link--default)', textDecoration: 'none' }}
      >
        Terms and Conditions
      </NavLink>
    </footer>
  );

  return (
    <>
      <Page
        mainContainerId={pageId}
        isManagedSidebar
        masthead={masthead}
        sidebar={Sidebar}
        skipToContent={PageSkipToContent}
        style={{ paddingBottom: '3rem' }}
      >
        {children}
      </Page>
      {footer}
    </>
  );
};

export { AppLayout };
